import { Button, Div, DivProps, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import React from 'react';

export interface MainCoverageProsConsProps extends DivProps {
  pros: string[];
  cons: string[];
  maxPros?: number;
  showText?: string;
  hideText?: string;
}

export const MainCoverageProsCons: React.VFC<MainCoverageProsConsProps> = ({
  pros,
  cons,
  maxPros = 3,
  showText,
  hideText,
  ...rest
}) => {
  const canBeHidden = React.useMemo(
    () => !!(showText && hideText),
    [showText, hideText]
  );
  const [isHidden, setIsHidden] = React.useState<boolean>(canBeHidden);
  React.useEffect(() => {
    setIsHidden(canBeHidden);
  }, [canBeHidden]);
  const buttonText = React.useMemo(
    () => (isHidden ? showText : hideText),
    [showText, hideText, isHidden]
  );
  const onClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsHidden((b) => !b);
    },
    [setIsHidden]
  );
  return (
    <Div data-testid="MainCoverageProsCons" {...rest}>
      {pros.map((item, idx) => (
        <MainCoverageProsConsItem
          key={idx}
          text={item}
          type="pros"
          display={[
            isHidden && idx + 1 > maxPros ? 'none' : 'flex',
            null,
            'flex',
          ]}
          data-testid="MainCoverageProsCons.pros"
        />
      ))}
      {cons.map((item, idx) => (
        <MainCoverageProsConsItem
          key={idx}
          text={item}
          type="danger"
          display={[isHidden ? 'none' : 'flex', null, 'flex']}
          data-testid="MainCoverageProsCons.cons"
        />
      ))}
      {canBeHidden && (
        <Button
          variant="transparent"
          onClick={onClick}
          display={[null, null, 'none']}
          mt={2}
          data-testid="MainCoverageProsCons.button"
        >
          {buttonText}
        </Button>
      )}
    </Div>
  );
};

interface MainCoverageProsConsItemProps extends DivProps {
  text: string;
  type: 'pros' | 'danger';
}

const MainCoverageProsConsItem: React.FC<MainCoverageProsConsItemProps> = ({
  text,
  type,
  ...restProps
}) => {
  let divColor = '';
  let iconName = '';
  let variant = '';

  switch (type) {
    case 'danger': {
      divColor = 'second.0';
      iconName = 'small-cancel';
      variant = 'dangerBig';
      break;
    }
    case 'pros': {
      iconName = 'green-check';
      variant = 'successBig';
      break;
    }
  }

  return (
    <Div
      display="flex"
      my="auto"
      gridGap={1}
      data-testid="MainCoverageProsConsItem"
      {...restProps}
    >
      <IconV2
        name={iconName}
        variant={variant}
        width={[20, null, 28]}
        height={[20, null, 28]}
        minHeight={[20, null, 28]}
        data-testid="MainCoverageProsConsItem.icon"
      />
      <P
        // @ts-ignore color type is broken, should not be string[] & string, should be string[] | string..
        color={divColor ? [null, null, divColor] : divColor}
        mt="3px"
        variant="footnoteRegularToCaption"
        data-testid="MainCoverageProsConsItem.text"
      >
        {text}
      </P>
    </Div>
  );
};
